import { Tooltip } from 'antd';
import React from 'react';
import { FaBookOpen } from 'react-icons/fa';
import { IoIosArrowDropdownCircle, IoIosHome } from 'react-icons/io';
import { NavLink, useMatch } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import OpterraPhoenixLogo from '../../../assets/images/OpterraPhoenixLogo.png';
import {
  FARMS_PAGE_PATH,
  GC_EDITOR_ROLE,
  GLOBAL_CATALOGUE_PATH,
  HOME_PAGE_PATH,
} from '../../../constants/Constant';
import { useAuth } from '../../../context/AuthContext';
import { hasUserRole } from '../../../utils/AuthUtils';
import MergeClassNames from '../../../utils/MergeClassNames';
import { RootState } from 'redux/Store';
import { setSidebarCollapsed } from '../../../redux/slices/FarmSlice';

interface ILeftNavRailProps {
  mobileMenuOpen?: boolean;
  onClose?: () => void;
}

const NavTooltipContent = ({
  label,
  description,
}: {
  label: string;
  description: string;
}) => (
  <>
    {label}
    <br />
    <span className="opacity-75">{description}</span>
  </>
);

const NavItem = ({
  to,
  label,
  description,
  icon,
  onClick,
  className,
  iconClassName,
}: {
  to: string;
  label: string;
  description: string;
  icon: React.ReactNode;
  onClick?: () => void;
  className?: string;
  iconClassName?: string;
}) => (
  <Tooltip
    title={<NavTooltipContent label={label} description={description} />}
    placement="right"
  >
    <NavLink to={to} onClick={onClick}>
      {({ isActive }) => (
        <div
          className={MergeClassNames(
            'flex flex-col items-center justify-center',
            className,
            isActive ? 'text-success' : 'text-gray-dark hover:text-success'
          )}
        >
          <div
            className={MergeClassNames(
              'flex h-7 items-center justify-center p-1',
              iconClassName,
              isActive &&
                'mb-1 rounded-md border border-b-4 border-success bg-white'
            )}
          >
            {icon}
          </div>
          <span className="text-center text-xs">{label}</span>
        </div>
      )}
    </NavLink>
  </Tooltip>
);

const LeftNavRail: React.FC<ILeftNavRailProps> = ({
  mobileMenuOpen = false,
  onClose,
}) => {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const showCatalogue = hasUserRole(user, GC_EDITOR_ROLE);
  const sidebarCollapsed = useSelector(
    (state: RootState) => state.farmReducer.sidebarCollapsed
  );
  const isFarmsPage = !!useMatch({ path: FARMS_PAGE_PATH, end: false });

  return (
    <>
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-20 bg-black bg-opacity-50 md:hidden"
          onClick={onClose}
        />
      )}

      <nav
        className={MergeClassNames(
          'fixed left-0 top-0 z-20 flex h-full flex-col items-center bg-gray-bright py-4 transition-transform duration-300',
          'md:w-16 md:translate-x-0',
          mobileMenuOpen ? 'w-16 translate-x-0' : 'w-16 -translate-x-full',
          // Collapsed LH tree panel: the rail becomes the panel's right edge,
          // so it picks up the border that edge would otherwise be missing.
          isFarmsPage && sidebarCollapsed && 'border-r border-secondary'
        )}
      >
        <NavLink
          to={HOME_PAGE_PATH}
          className="mb-3 flex items-center justify-center"
          onClick={onClose}
        >
          <img src={OpterraPhoenixLogo} alt="Logo" className="h-8 w-auto" />
        </NavLink>

        {isFarmsPage && sidebarCollapsed && (
          <Tooltip
            title={
              <NavTooltipContent
                label="Sidebar"
                description="Expand the farm tree panel"
              />
            }
            placement="right"
          >
            <button
              type="button"
              aria-label="Expand sidebar"
              className="mb-2 flex flex-col items-center justify-center text-gray-dark hover:opacity-75"
              onClick={() => dispatch(setSidebarCollapsed(false))}
            >
              <div className="p-1">
                {/* Same chevron used to collapse the panel, mirrored to show
                    the opposite (expand) direction. */}
                <IoIosArrowDropdownCircle
                  size={18}
                  className="-rotate-90 transition-transform duration-150 hover:scale-[2]"
                />
              </div>
              <span className="text-center text-xs">Sidebar</span>
            </button>
          </Tooltip>
        )}

        <div className="flex flex-col items-center space-y-2">
          <NavItem
            to={FARMS_PAGE_PATH}
            label="Home"
            description="Browse farms and paddocks"
            icon={<IoIosHome size={22} />}
            onClick={onClose}
          />

          {showCatalogue && (
            <NavItem
              to={GLOBAL_CATALOGUE_PATH}
              label="Catalogue"
              description="Browse the global catalogue"
              icon={<FaBookOpen size={22} />}
              onClick={onClose}
            />
          )}
        </div>
      </nav>
    </>
  );
};

export default LeftNavRail;
